import React, { useState, useEffect } from 'react';

// Import reusable components
import Slider from '../Slider';
import Testimonials from '../Testimonials';
import FAQ from '../FAQ';
import InvestmentSection from '../investments/InvestmentSection';

// Import landing page sections
import LandingHeader from '../sections/LandingHeader';
import Hero from '../sections/Hero';
import FlagshipShowcase from '../sections/FlagshipShowcase';
import Features from '../sections/Features';
import ApplicationForm from '../sections/ApplicationForm';
import StatusCheck from '../sections/StatusCheck';
import About from '../sections/About';
import CallToAction from '../sections/CallToAction';
import LandingFooter from '../sections/LandingFooter';
import { API_BASE_URL } from '../../config';

// Component mapping - maps component names in JSON to actual React components
const COMPONENT_MAP = {
  // Landing page sections
  'LandingHeader': LandingHeader,
  'Hero': Hero,
  'FlagshipShowcase': FlagshipShowcase,
  'Features': Features,
  'ApplicationForm': ApplicationForm,
  'StatusCheck': StatusCheck,
  'About': About,
  'CallToAction': CallToAction,
  'LandingFooter': LandingFooter,
  
  // Reusable components
  'Slider': Slider,
  'Testimonials': Testimonials,
  'FAQ': FAQ,
  'InvestmentSection': InvestmentSection
};

// Individual component renderer
const ComponentRenderer = ({ section, tenant, stats }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const Component = COMPONENT_MAP[section.component];

  // Fetch data if component has a dataSource.
  // Order: 1) DB-backed CMS (instant edits), 2) static JSON file fallback.
  // dataSource examples: "components/hero.json", "testimonials.json"
  useEffect(() => {
    if (!section.dataSource) return;
    setLoading(true);

    // Derive section key from the file path: "components/hero.json" -> "hero"
    const sectionKey = section.dataSource
      .replace(/^components\//, '')
      .replace(/\.json$/, '');

    const tryDb = async () => {
      try {
        const resolvedTenantId = section.tenantId || window.__CHOICE_TENANT_ID || tenant;
        if (!resolvedTenantId) return null;
        const res = await fetch(`${API_BASE_URL}/api/tenant-content/${resolvedTenantId}/${sectionKey}`);
        if (!res.ok) return null;
        const json = await res.json();
        return json?.content || null;
      } catch { return null; }
    };

    const tryFile = async () => {
      const res = await fetch(`/tenant-content/${tenant}/${section.dataSource}`);
      if (!res.ok) throw new Error(`Failed to load ${section.dataSource}`);
      return res.json();
    };

    (async () => {
      try {
        const dbData = await tryDb();
        const finalData = dbData || (await tryFile());
        setData(finalData);
        setLoading(false);
      } catch (err) {
        console.error(`Error loading ${section.dataSource}:`, err);
        setError(err.message);
        setLoading(false);
      }
    })();
  }, [section.dataSource, section.tenantId, tenant]);

  // Component not found
 if (!Component) {
    console.error(`❌ Component "${section.component}" not found in COMPONENT_MAP`);
    console.log('Available components:', Object.keys(COMPONENT_MAP));
    return null;
}

console.log(`✅ Rendering ${section.component}`, section);

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner">Loading {section.component}...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <p>Error loading {section.component}: {error}</p>
      </div>
    );
  }

  // Render component with props
  return (
    <Component 
      data={data} 
      stats={stats}
      {...section.props} 
    />
  );
};

// Main Component Registry
const ComponentRegistry = ({ tenant = 'choice' }) => {
  const [layout, setLayout] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch layout configuration and stats
  useEffect(() => {
    Promise.all([
      fetch(`/tenant-content/${tenant}/layout.json`).then(res => res.json()),
     // fetch('https://svc.choicemcn.com/api/analytics/overview/public').then(res => res.json())
	 fetch(`${API_BASE_URL}/api/analytics/overview/public`).then(res => res.json())
    ])
      .then(([layoutData, statsData]) => {
        setLayout(layoutData);
        setStats(statsData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading layout or stats:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [tenant]);

  // Update document meta tags using native DOM
  useEffect(() => {
    if (!layout?.meta) return;

    const meta = layout.meta;

    // Update title
    if (meta.title) {
      document.title = meta.title;
    }

    // Helper to update or create meta tag
    const updateMeta = (selector, attributes) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        Object.keys(attributes).forEach(key => {
          element.setAttribute(key, attributes[key]);
        });
        document.head.appendChild(element);
      } else {
        Object.keys(attributes).forEach(key => {
          element.setAttribute(key, attributes[key]);
        });
      }
    };

    // Helper to update or create link tag
    const updateLink = (selector, attributes) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        Object.keys(attributes).forEach(key => {
          element.setAttribute(key, attributes[key]);
        });
        document.head.appendChild(element);
      } else {
        Object.keys(attributes).forEach(key => {
          element.setAttribute(key, attributes[key]);
        });
      }
    };

    // Basic meta tags
    if (meta.description) updateMeta('meta[name="description"]', { name: 'description', content: meta.description });
    if (meta.keywords) updateMeta('meta[name="keywords"]', { name: 'keywords', content: meta.keywords });
    if (meta.author) updateMeta('meta[name="author"]', { name: 'author', content: meta.author });
    if (meta.robots) updateMeta('meta[name="robots"]', { name: 'robots', content: meta.robots });
    if (meta.themeColor) updateMeta('meta[name="theme-color"]', { name: 'theme-color', content: meta.themeColor });

    // Favicon and icons
    if (meta.links) {
      if (meta.links.icon) updateLink('link[rel="icon"]', { rel: 'icon', href: meta.links.icon });
      if (meta.links.icon16) updateLink('link[rel="icon"][sizes="16x16"]', { rel: 'icon', type: 'image/png', sizes: '16x16', href: meta.links.icon16 });
      if (meta.links.icon32) updateLink('link[rel="icon"][sizes="32x32"]', { rel: 'icon', type: 'image/png', sizes: '32x32', href: meta.links.icon32 });
      if (meta.links.appleTouchIcon180) updateLink('link[rel="apple-touch-icon"]', { rel: 'apple-touch-icon', sizes: '180x180', href: meta.links.appleTouchIcon180 });
      if (meta.links.manifest) updateLink('link[rel="manifest"]', { rel: 'manifest', href: meta.links.manifest });
    }

    // Open Graph
    if (meta.openGraph) {
      const og = meta.openGraph;
      if (og.type) updateMeta('meta[property="og:type"]', { property: 'og:type', content: og.type });
      if (og.title) updateMeta('meta[property="og:title"]', { property: 'og:title', content: og.title });
      if (og.description) updateMeta('meta[property="og:description"]', { property: 'og:description', content: og.description });
      if (og.url) updateMeta('meta[property="og:url"]', { property: 'og:url', content: og.url });
      if (og.siteName) updateMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: og.siteName });
      if (og.image) updateMeta('meta[property="og:image"]', { property: 'og:image', content: og.image });
      if (og.locale) updateMeta('meta[property="og:locale"]', { property: 'og:locale', content: og.locale });
    }

    // Twitter Card
    if (meta.twitter) {
      const tw = meta.twitter;
      if (tw.card) updateMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: tw.card });
      if (tw.site) updateMeta('meta[name="twitter:site"]', { name: 'twitter:site', content: tw.site });
      if (tw.creator) updateMeta('meta[name="twitter:creator"]', { name: 'twitter:creator', content: tw.creator });
      if (tw.title) updateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: tw.title });
      if (tw.description) updateMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: tw.description });
      if (tw.image) updateMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: tw.image });
    }

    // Canonical
    if (meta.canonical) updateLink('link[rel="canonical"]', { rel: 'canonical', href: meta.canonical });

  }, [layout]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div className="spinner">Loading platform...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <h2>Error Loading Platform</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!layout) {
    return <div>No layout configuration found</div>;
  }

  return (
    <>
      {layout.sections.filter(section => section.enabled).map((section, index) => (
        <ComponentRenderer
          key={section.id || `section-${index}`}
          section={section}
          tenant={tenant}
          stats={stats}
        />
      ))}
    </>
  );
};

export default ComponentRegistry;